/**
 * AuthLayout Component (Fixed Version)
 * 
 * This version of the AuthLayout component uses CSS variables instead of
 * styled-components' theme prop to avoid the "Cannot read properties of undefined (reading 'theme')" error.
 * 
 * It follows Module M7 (Integration Points) principles by providing a clear
 * integration point between the component and the theme system.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { branding, showLogoText, showLogo, showTagline, getCopyright } from '../../config/branding';

// Video background component
const VideoBackground = styled.video`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
`;

// Use CSS variables instead of theme props
const AuthContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
  /* Remove background-color to show video */
`;

const AuthHeader = styled.header`
  padding: var(--spacing-lg);
  text-align: center;
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--primary-500);
  margin-bottom: var(--spacing-md);

  img {
    height: 48px;
    width: auto;
  }
`;

const AuthContent = styled.main`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-lg);
`;

const AuthCard = styled.div`
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  padding: var(--spacing-xl);
  width: 100%;
  max-width: 480px;
`;

const AuthFooter = styled.footer`
  padding: var(--spacing-md);
  text-align: center;
  color: var(--text-tertiary);
  font-size: var(--font-size-sm);
`;

const KioskSection = styled.div`
  text-align: center;
  padding: var(--spacing-sm) 0;
`;

const KioskLink = styled(Link)`
  display: inline-block;
  padding: var(--spacing-xs) var(--spacing-md);
  color: white;
  font-size: var(--font-size-xs);
  text-decoration: none;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: all 0.2s ease;
  opacity: 0.8;

  &:hover {
    color: var(--primary-600);
    border-color: white;
    background-color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    opacity: 1;
  }
`;

interface AuthLayoutProps {
  children: React.ReactNode;
}

/**
 * AuthLayout Component
 * 
 * This component provides a consistent layout for authentication pages.
 * It uses CSS variables instead of styled-components' theme prop to avoid
 * the "Cannot read properties of undefined (reading 'theme')" error.
 */
const AuthLayoutFixed: React.FC<AuthLayoutProps> = ({ children }) => {
  const location = useLocation();
  const isKioskLogin = location.pathname === '/kiosk/login';

  return (
    <AuthContainer>
      <VideoBackground
        autoPlay
        loop
        muted
        playsInline
      >
        <source
          src="https://video.gumlet.io/67b89d2033456fa6739dba86/67b89d7033456fa6739dbc39/download.mp4"
          type="video/mp4"
        />
      </VideoBackground>

      <AuthHeader>
        <Logo>
          {showLogo() && <img src={branding.logo} alt={branding.logoWithText ? branding.logoAlt : ''} />}
          {showLogoText() && branding.appName}
        </Logo>
        {showTagline() && <p>{branding.appTagline}</p>}
      </AuthHeader>

      <AuthContent>
        <AuthCard>{children}</AuthCard>
      </AuthContent>

      {!isKioskLogin && (
        <KioskSection>
          <KioskLink to="/kiosk/login">
            🖥️ Kiosk Mode
          </KioskLink>
        </KioskSection>
      )}

      {getCopyright() && (
        <AuthFooter>
          {getCopyright()}
        </AuthFooter>
      )}
    </AuthContainer>
  );
};

export default AuthLayoutFixed;